import { Action, createReducer, on } from '@ngrx/store';
import { InsightsLibraryActions } from '.';
import { DataModelDto, ListUserDashboardDto, ListUserVisualDto } from '../../../services/api.services';
import { isWhitespaceOnly } from '../../../shared/utils';
import { createItemSelectorReducer, createItemSelectorState, ItemSelectorState } from '../item-selector/reducer';
import { InsightsLibraryDashboard, InsightsLibraryDashboardCategory, InsightsLibraryPage, InsightsLibraryVisual, InsightsLibraryVisualCategory } from './types';

export interface State {
  visible: boolean;
  page: InsightsLibraryPage;
  starredOnly: boolean;
  filter: string;

  // Generic tracker for any update operation. We only allow one operation at a time.
  updating: boolean;

  loadingDashboards: boolean;
  dashboards: ListUserDashboardDto[];

  loadingVisuals: boolean;
  visuals: ListUserVisualDto[];

  copyingVisual: boolean;

  viewMode: boolean;
};

export const initialState: State = {
  visible: false,
  page: InsightsLibraryPage.Insights,
  starredOnly: false,
  filter: '',

  updating: false,

  loadingDashboards: false,
  dashboards: [],

  loadingVisuals: false,
  visuals: [],

  copyingVisual: false,

  viewMode: false,
};

const insightsLibraryReducer = createReducer(
  initialState,


  on(InsightsLibraryActions.setVisible, (state, { visible }) => {
    return {
      ...state,
      visible,
      viewMode: false // view mode is always disabled on enter/exit - don't want the embed dangling around
    };
  }),

  on(InsightsLibraryActions.setPage, (state, { page }) => {
    return {
      ...state,
      page,
    };
  }),

  on(InsightsLibraryActions.setStarredOnly, (state, { starredOnly }) => {
    return {
      ...state,
      starredOnly,
    };
  }),

  on(InsightsLibraryActions.setFilter, (state, { filter }) => {
    let normalizedFilter = (filter ?? '').trim();

    if (isWhitespaceOnly(normalizedFilter))
      normalizedFilter = null;

    return {
      ...state,
      filter: normalizedFilter,
    };
  }),

  on(InsightsLibraryActions.toggleViewMode, (state, { viewMode }) => {
    return {
      ...state,
      viewMode,
    };
  }),


  on(InsightsLibraryActions.loadDashboards, (state) => {
    return {
      ...state,
      loadingDashboards: true
    };
  }),

  on(InsightsLibraryActions.loadDashboardsSuccess, (state, { dashboards }) => {
    return {
      ...state,
      loadingDashboards: false,
      dashboards
    };
  }),

  on(InsightsLibraryActions.loadDashboardsFailure, (state, { error }) => {
    return {
      ...state,
      loadingDashboards: false,
      dashboards: []
    };
  }),


  on(InsightsLibraryActions.loadVisuals, (state) => {
    return {
      ...state,
      loadingVisuals: true
    };
  }),

  on(InsightsLibraryActions.loadVisualsSuccess, (state, { visuals }) => {
    return {
      ...state,
      loadingVisuals: false,
      visuals
    };
  }),

  on(InsightsLibraryActions.loadVisualsFailure, (state, { error }) => {
    return {
      ...state,
      loadingVisuals: false
    };
  }),

  on(
    InsightsLibraryActions.copyDashboard,
    InsightsLibraryActions.copyVisual, (state) => ({
      ...state,
      updating: true,
      copying: true
    })
  ),

  on(
    InsightsLibraryActions.copyDashboardSuccess,
    InsightsLibraryActions.copyDashboardFailure,
    InsightsLibraryActions.copyVisualSuccess,
    InsightsLibraryActions.copyVisualFailure, (state) => ({
      ...state,
      updating: false,
      copying: false
    })
  ),
);

export function reducer(state: State | undefined, action: Action) {
  return insightsLibraryReducer(state, action);
}

//#region Dashboard & Visual Item Selectors
export const dashboardItemSelectorInitialState =
  createItemSelectorState<InsightsLibraryDashboard, InsightsLibraryDashboardCategory>();

const insightsLibraryDashboardItemSelectorReducer =
  createItemSelectorReducer('Dashboards', dashboardItemSelectorInitialState);

export function dashboardItemSelectorReducer(state: ItemSelectorState<InsightsLibraryDashboard, InsightsLibraryDashboardCategory> | undefined, action: Action) {
  return insightsLibraryDashboardItemSelectorReducer(state, action);
}


export const visualItemSelectorInitialState =
  createItemSelectorState<InsightsLibraryVisual, InsightsLibraryVisualCategory>();

const insightsLibraryVisualItemSelectorReducer =
  createItemSelectorReducer('Visuals', visualItemSelectorInitialState);

export function visualItemSelectorReducer(state: ItemSelectorState<InsightsLibraryVisual, InsightsLibraryVisualCategory> | undefined, action: Action) {
  return insightsLibraryVisualItemSelectorReducer(state, action);
}
//#endregion
